import type { Product, Discount } from '@/payload-types'
import { extractIdsFromRelationship } from '@/lib/discounts'

export interface DiscountBadge {
  kind: 'manual' | 'bulk' | 'automatic'
  percent?: number
  tierQty?: number
  tierPrice?: number | null
}

export interface ProductDiscountInfo {
  hasVariants: boolean
  hasBulkDiscount: boolean
  hasManualDiscount: boolean
  manualDiscountPercentage?: number
  bulkDiscountPercentage?: number
  tierQty?: number
  tierPrice?: number | null
  badge: DiscountBadge | null
}

export function getProductDiscountInfo(
  product: Partial<Product>,
  activeDiscounts?: Discount[],
): ProductDiscountInfo {
  const result: ProductDiscountInfo = {
    hasVariants: false,
    hasBulkDiscount: false,
    hasManualDiscount: false,
    badge: null,
  }

  // Check if product has variants
  // We check for enableVariants being true OR if variants array has items
  // This handles cases where enableVariants might be missing but variants exist
  if (product.enableVariants || (product.variants && product.variants.length > 0)) {
    result.hasVariants = true
  }

  // If no active discounts, return default result
  if (!activeDiscounts || activeDiscounts.length === 0) {
    return result
  }

  // Check each active discount
  for (const discount of activeDiscounts) {
    if (!discount.is_active) continue

    // Check if discount applies to this product
    let appliesToProduct = false

    if (discount.appliesTo === 'all') {
      appliesToProduct = true
    } else if (discount.appliesTo === 'product' && discount.products) {
      const productIds = extractIdsFromRelationship(discount.products)
      appliesToProduct = productIds.includes(product.id!)
    } else if (discount.appliesTo === 'category' && discount.category) {
      const categoryIds = extractIdsFromRelationship(discount.category)
      const productCategoryIds = extractIdsFromRelationship(product.categories)
      appliesToProduct = productCategoryIds.some((id) => categoryIds.includes(id))
    }

    if (!appliesToProduct) continue

    // Check discount type and percentage
    if (discount.discountMode === 'bulk') {
      result.hasBulkDiscount = true

      if (
        discount.apply_as_volume_pricing &&
        Array.isArray(discount.volumePricing) &&
        discount.volumePricing.length > 0
      ) {
        // Use highest discount percentage for backward compatibility
        const maxTierDiscount = Math.max(
          ...discount.volumePricing.map((t) => t.discount_percentage || 0),
        )
        result.bulkDiscountPercentage = maxTierDiscount

        // Entry tier = lowest quantity
        const validTiers = discount.volumePricing.filter(
          (t) => typeof t.quantity === 'number' && t.quantity > 0,
        )

        if (validTiers.length > 0) {
          const entryTier = validTiers.reduce((min, t) =>
            t.quantity < min.quantity ? t : min,
          )

          if (result.hasVariants || typeof product.basePrice !== 'number') {
            result.tierQty = undefined
            result.tierPrice = null
            result.badge = {
              kind: 'bulk',
              tierQty: undefined,
              tierPrice: null,
            }
          } else {
            const tierQty = entryTier.quantity
            const tierPrice = calculateDiscountedPrice(
              product.basePrice,
              entryTier.discount_percentage || 0,
            )
            result.tierQty = tierQty
            result.tierPrice = tierPrice
            result.badge = {
              kind: 'bulk',
              tierQty,
              tierPrice,
            }
          }
        } else {
          result.tierQty = undefined
          result.tierPrice = null
          result.badge = {
            kind: 'bulk',
            tierQty: undefined,
            tierPrice: null,
          }
        }
      } else {
        // Flat bulk without volume pricing
        if (discount.discount_percentage) {
          result.bulkDiscountPercentage = discount.discount_percentage
        }

        if (discount.bulk_quantity) {
          if (result.hasVariants || typeof product.basePrice !== 'number') {
            result.tierQty = undefined
            result.tierPrice = null
            result.badge = {
              kind: 'bulk',
              tierQty: undefined,
              tierPrice: null,
            }
          } else {
            const tierQty = discount.bulk_quantity
            const tierPrice = discount.discount_percentage
              ? calculateDiscountedPrice(product.basePrice, discount.discount_percentage)
              : null
            result.tierQty = tierQty
            result.tierPrice = tierPrice
            result.badge = {
              kind: 'bulk',
              tierQty,
              tierPrice,
            }
          }
        } else {
          // If bulk_quantity is missing, show plain MENGENRABATT pill
          result.tierQty = undefined
          result.tierPrice = null
          result.badge = {
            kind: 'bulk',
            tierQty: undefined,
            tierPrice: null,
          }
        }
      }
    } else if (discount.discountMode === 'manual' && discount.discount_percentage) {
      result.hasManualDiscount = true
      result.manualDiscountPercentage = discount.discount_percentage
      result.badge = {
        kind: 'manual',
        percent: discount.discount_percentage,
      }
    } else if (discount.discountMode === 'automatic') {
      // Automatic: ribbon only when appliesTo === 'all'
      if (discount.appliesTo === 'all' && discount.discount_percentage) {
        result.badge = {
          kind: 'automatic',
          percent: discount.discount_percentage,
        }
      }
    }
    // Coupon: nothing on card
  }

  return result
}

/**
 * Calculate the price after applying a discount percentage.
 * Rounds to 2 decimal places to avoid floating-point artifacts.
 */
export function calculateDiscountedPrice(
  originalPrice: number,
  discountPercentage: number,
): number {
  const discounted = originalPrice * (1 - discountPercentage / 100)
  return Math.round(discounted * 100) / 100
}
